import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { MagnifyingGlassIcon as SearchIcon } from '@heroicons/react/24/outline';
import { Hit } from 'instantsearch.js';
import { Highlight } from 'react-instantsearch-hooks-web';
import { productsIndex } from '@loomstateorg/shared-frontend/constants/algolia';
import { Category } from '@loomstateorg/shared-frontend/types/category';
import Link from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/link';

export interface Props {
  hit: Hit<
    {
      query: string;
    } & {
      [key: string]: {
        exact_nb_hits: number;
        facets: {
          exact_matches: {
            [key: string]: [
              {
                value: string;
                count: number;
              },
            ];
          };
        };
      };
    }
  >;
  categories: Category[];
  onClick?: () => void;
  index?: number;
}

const SearchSuggestion: React.FC<Props> = ({ hit, categories, onClick, index = 0 }) => {
  const router = useRouter();

  const categoryOptions = useMemo(() => {
    return hit[productsIndex].facets.exact_matches['categories.categoryId']
      .map(({ value }) => categories.find((category) => category.categoryId === value) as Category)
      .filter(Boolean);
  }, [hit, categories]);

  const handleQuerySuggestionClick = useCallback(() => {
    onClick?.();

    router.push(`/search?q=${hit.query}`);
  }, [router, hit, onClick]);

  const handleCategoryOptionClick = useCallback(
    ({ path }: Partial<Category>) => {
      onClick?.();

      router.push(`${path}?q=${hit.query}`);
    },
    [router, hit, onClick],
  );

  return (
    <div className="flex items-start gap-28" data-testid={`atom-search-suggestion-${index}`}>
      <SearchIcon className="size-24 stroke-secondary-regular" />
      <div className="flex flex-col gap-24">
        <Link
          link={`/search?q=${hit.query}`}
          onMouseUp={handleQuerySuggestionClick}
          data-testid={`atom-search-suggestion-query-${index}`}
        >
          <Highlight
            hit={hit}
            attribute="query"
            nonHighlightedTagName="b"
            highlightedTagName="span"
            classNames={{ nonHighlighted: 'font-semibold' }}
          />
        </Link>
        {categoryOptions.map(({ categoryId, name, path }, categoryIndex) => (
          <Link
            key={categoryId}
            link={`${path}?q=${hit.query}`}
            onMouseUp={() => handleCategoryOptionClick({ path })}
            data-testid={`atom-search-suggestion-category-${index}-${categoryIndex}`}
          >
            <i className="text-secondary-regular">In {name}</i>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SearchSuggestion;
